import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { StayAmenity, StayRoomType, StaySortOption } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import { createPriceMarkerIcon } from '../../components/map/createPriceMarkerIcon';
import { Modal } from '../../components/modal';
import SwitchComponent from '../../components/switch';
import {
  Alert,
  EmptyState,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  Muted,
  Select,
  Stack,
  Strong,
} from '../../components/ui';
import { useSearchStaysQuery } from '../../services/stayService/__generated__/queries.generated';
import { AMENITY_GROUPS, ROOM_TYPE_LABELS, amenityLabel } from '../StayHostPage/stayForm';
import { useSearchNavSlot } from '../../components/navBar/searchNavSlot';
import { CategoryBar } from './StayCategoryBar';
import { StayCard } from './StayGuestComponents';
import { StaySearchBar } from './StaySearchBar';
import { resultsHeading } from './stayHomeRows';
import {
  activeFilterCount,
  EMPTY_FILTERS,
  parseStaySearch,
  staySearchQuery,
  toSearchStaysInput,
  type StayFilters,
  type StaySearchState,
} from './staySearchState';

const PAGE_SIZE = 18;
const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = ['places'];

const TOOLBAR_HEIGHT = 86;

const Toolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 30;
  padding: 12px 24px 0;
  background: var(--color-bg-elevated, #fff);
  border-bottom: 1px solid var(--color-border);
  @media (min-width: 744px) {
    padding: 12px 40px 0;
  }
`;

const FiltersButton = styled.button`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  margin-bottom: 12px;
  padding: 0 16px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    border-color: var(--color-text-primary);
    background: var(--color-bg-secondary);
  }
  em {
    font-style: normal;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 9999px;
    background: var(--color-text-primary);
    color: var(--color-bg-elevated, #fff);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
  }
`;

const Layout = styled.div`
  display: flex;
  width: 100%;
  min-height: calc(100vh - 68px - ${TOOLBAR_HEIGHT}px);
`;

const ListColumn = styled.section<{ $hiddenOnPhone: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 24px 24px 96px;
  @media (min-width: 744px) {
    padding: 24px 40px 96px;
  }
  @media (min-width: 1128px) {
    padding-right: 24px;
  }
  @media (max-width: 1127px) {
    display: ${({ $hiddenOnPhone }) => ($hiddenOnPhone ? 'none' : 'block')};
  }
`;

const MapColumn = styled.aside<{ $shownOnPhone: boolean }>`
  position: sticky;
  top: ${TOOLBAR_HEIGHT}px;
  flex: 0 0 42%;
  height: calc(100vh - ${TOOLBAR_HEIGHT}px);
  padding: 24px 24px 24px 0;
  @media (max-width: 1127px) {
    display: ${({ $shownOnPhone }) => ($shownOnPhone ? 'block' : 'none')};
    flex-basis: 100%;
    top: 0;
    height: calc(100vh - 68px - ${TOOLBAR_HEIGHT}px);
    padding: 0;
  }
`;

const MapFrame = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid var(--color-border);
  @media (max-width: 1127px) {
    border-radius: 0;
    border: none;
  }
`;

const ResultsHead = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 24px;
`;

const Count = styled.h1`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const SortSelect = styled.select`
  width: auto;
  height: 36px;
  padding: 0 32px 0 14px;
  border: 1px solid var(--color-border);
  border-radius: 9999px;
  background-color: var(--color-bg-elevated, #fff);
  font-size: 14px;
  color: var(--color-text-primary);
  cursor: pointer;
`;

const ResultsGrid = styled.div`
  display: grid;
  column-gap: 24px;
  row-gap: 40px;
  grid-template-columns: 1fr;
  @media (min-width: 550px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: 950px) and (max-width: 1127px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (min-width: 1640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const MapToggle = styled.button`
  position: fixed;
  z-index: 20;
  left: 50%;
  bottom: 32px;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 48px;
  padding: 0 20px;
  border: none;
  border-radius: 9999px;
  background: #222;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 0.15s ease;
  &:hover {
    transform: translateX(-50%) scale(1.04);
  }
  @media (min-width: 1128px) {
    display: none;
  }
`;

const AreaButton = styled.div`
  ${tw`absolute z-10`}
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
`;

const Chips = styled.div`
  ${tw`flex flex-wrap gap-2`}
`;

const Chip = styled.button<{ $on: boolean }>`
  ${tw`px-3 py-2 text-sm cursor-pointer`}
  border-radius: 9999px;
  border: 1px solid ${({ $on }) => ($on ? 'var(--color-text-primary)' : 'var(--color-border)')};
  background: ${({ $on }) =>
    $on ? 'var(--color-bg-secondary)' : 'var(--color-bg-elevated, #fff)'};
  color: var(--color-text-primary);
`;

const Pager = styled.div`
  ${tw`flex items-center justify-center gap-3 mt-10`}
`;

/** Filters (spec 016 W12): price, type of place, rooms, amenities, booking options. */
function FiltersModal({
  open,
  value,
  onClose,
  onApply,
}: {
  open: boolean;
  value: StayFilters;
  onClose: () => void;
  onApply: (filters: StayFilters) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [lastOpen, setLastOpen] = useState(false);
  if (open && !lastOpen) {
    setLastOpen(true);
    setDraft(value);
  } else if (!open && lastOpen) {
    setLastOpen(false);
  }
  const number = (raw: string) => (raw === '' ? undefined : Number(raw));
  const toggle = <T,>(items: T[], item: T) =>
    items.includes(item) ? items.filter(x => x !== item) : [...items, item];

  return (
    <Modal isOpen={open} onClose={onClose} title="Filters" modalWidth="640px">
      <Stack $gap={6} data-testid="stay-filters">
        <h3 style={{ margin: 0 }}>Filters</h3>
        <div>
          <Strong>Price per night</Strong>
          <FormGrid $cols={2}>
            <Field label="Minimum" htmlFor="filter-min-price">
              <Input
                id="filter-min-price"
                inputMode="numeric"
                value={draft.minPrice ?? ''}
                onChange={e => setDraft({ ...draft, minPrice: number(e.target.value) })}
              />
            </Field>
            <Field label="Maximum" htmlFor="filter-max-price">
              <Input
                id="filter-max-price"
                inputMode="numeric"
                value={draft.maxPrice ?? ''}
                onChange={e => setDraft({ ...draft, maxPrice: number(e.target.value) })}
              />
            </Field>
          </FormGrid>
        </div>
        <div>
          <Strong>Type of place</Strong>
          <Chips style={{ marginTop: 8 }}>
            {Object.values(StayRoomType).map(type => (
              <Chip
                key={type}
                type="button"
                $on={draft.roomTypes.includes(type)}
                aria-pressed={draft.roomTypes.includes(type)}
                onClick={() => setDraft({ ...draft, roomTypes: toggle(draft.roomTypes, type) })}
              >
                {ROOM_TYPE_LABELS[type].title}
              </Chip>
            ))}
          </Chips>
        </div>
        <FormGrid $cols={3}>
          {(
            [
              ['minBedrooms', 'Bedrooms'],
              ['minBeds', 'Beds'],
              ['minBathrooms', 'Bathrooms'],
            ] as const
          ).map(([key, label]) => (
            <Field key={key} label={label} htmlFor={`filter-${key}`}>
              <Select
                id={`filter-${key}`}
                value={draft[key] ?? ''}
                onChange={e => setDraft({ ...draft, [key]: number(e.target.value) })}
              >
                <option value="">Any</option>
                {[1, 2, 3, 4, 5, 6].map(n => (
                  <option key={n} value={n}>
                    {n}+
                  </option>
                ))}
              </Select>
            </Field>
          ))}
        </FormGrid>
        <div>
          <Strong>Amenities</Strong>
          <Chips style={{ marginTop: 8 }}>
            {AMENITY_GROUPS[0].items.concat(AMENITY_GROUPS[1].items).map((amenity: StayAmenity) => (
              <Chip
                key={amenity}
                type="button"
                $on={draft.amenities.includes(amenity)}
                aria-pressed={draft.amenities.includes(amenity)}
                onClick={() => setDraft({ ...draft, amenities: toggle(draft.amenities, amenity) })}
              >
                {amenityLabel(amenity)}
              </Chip>
            ))}
          </Chips>
        </div>
        <div>
          {(
            [
              ['instantBook', 'Instant Book', 'Book without waiting for the host'],
              ['selfCheckIn', 'Self check-in', 'Let yourself in'],
              ['freeCancellation', 'Free cancellation', 'Flexible or moderate policy'],
            ] as const
          ).map(([key, label, hint]) => (
            <div
              key={key}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 0',
              }}
            >
              <div>
                <Strong>{label}</Strong>
                <div>
                  <Muted $size="sm">{hint}</Muted>
                </div>
              </div>
              <SwitchComponent
                isActive={draft[key]}
                onChange={() => setDraft({ ...draft, [key]: !draft[key] })}
                inputTestId={`filter-${key}`}
                labelTestId={`filter-${key}-label`}
              />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            type="button"
            theme="text"
            text="Clear all"
            onClick={() => setDraft({ ...EMPTY_FILTERS, category: draft.category })}
          />
          <Button
            type="button"
            text="Show places"
            dataTestId="stay-filters-apply"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
          />
        </div>
      </Stack>
    </Modal>
  );
}

function ResultsMap({
  results,
  center,
  hoveredId,
  onSearchArea,
  onOpen,
}: {
  results: { id: string; lat: number; lng: number; price: number }[];
  center?: { lat: number; lng: number };
  hoveredId: string | null;
  onSearchArea: (bounds: StaySearchState['bounds']) => void;
  onOpen: (id: string) => void;
}) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });
  const mapRef = useRef<google.maps.Map | null>(null);
  const [moved, setMoved] = useState(false);
  const fallback = results[0]
    ? { lat: results[0].lat, lng: results[0].lng }
    : { lat: 39.5, lng: -98.35 };

  if (loadError) return <Muted $size="sm">The map could not load.</Muted>;
  if (!isLoaded) return <LoadingBlock label="Loading map" minHeight="100%" />;
  return (
    <div
      style={{ position: 'relative', width: '100%', height: '100%' }}
      data-testid="stay-results-map"
    >
      {moved && (
        <AreaButton>
          <Button
            type="button"
            theme="secondary"
            size="sm"
            text="Search this area"
            onClick={() => {
              const box = mapRef.current?.getBounds();
              if (!box) return;
              const ne = box.getNorthEast();
              const sw = box.getSouthWest();
              onSearchArea({ north: ne.lat(), east: ne.lng(), south: sw.lat(), west: sw.lng() });
              setMoved(false);
            }}
          />
        </AreaButton>
      )}
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center ?? fallback}
        zoom={center || results.length ? 11 : 4}
        onLoad={map => {
          mapRef.current = map;
        }}
        onDragEnd={() => setMoved(true)}
        onZoomChanged={() => mapRef.current && setMoved(true)}
        options={{ disableDefaultUI: true, zoomControl: true, clickableIcons: false }}
      >
        {results.map(result => (
          <Marker
            key={result.id}
            position={{ lat: result.lat, lng: result.lng }}
            icon={createPriceMarkerIcon(result.price, false, hoveredId === result.id)}
            zIndex={hoveredId === result.id ? 2 : 1}
            onClick={() => onOpen(result.id)}
          />
        ))}
      </GoogleMap>
    </div>
  );
}

function FiltersGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
    >
      <path d="M2 4h7M13 4h1M2 12h1M7 12h7" />
      <circle cx="11" cy="4" r="2" />
      <circle cx="5" cy="12" r="2" />
    </svg>
  );
}

/** Search results with filters and a map (spec 016 W12). The URL holds the search. */
export default function StaySearchPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => parseStaySearch(params), [params]);
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const navSlot = useSearchNavSlot();

  const update = (next: StaySearchState) => {
    setPage(1);
    setParams(staySearchQuery(next));
  };

  const { data, loading, error } = useSearchStaysQuery({
    variables: { input: toSearchStaysInput(state, page, PAGE_SIZE) },
    fetchPolicy: 'cache-and-network',
  });
  const result = data?.searchStays;
  const items = result?.items ?? [];
  const filterCount = activeFilterCount(state.filters);
  const openStay = (id: string) => {
    const query = new URLSearchParams();
    if (state.checkIn) query.set('checkIn', state.checkIn);
    if (state.checkOut) query.set('checkOut', state.checkOut);
    (['adults', 'children', 'infants', 'pets'] as const).forEach(key => {
      if (state.guests[key]) query.set(key, String(state.guests[key]));
    });
    navigate(`/stays/${id}${query.toString() ? `?${query}` : ''}`);
  };
  const markers = items
    .filter(item => item.listing.approximateLocation)
    .map(item => ({
      id: item.listing.id,
      lat: item.listing.approximateLocation!.latitude,
      lng: item.listing.approximateLocation!.longitude,
      price: item.quote
        ? item.quote.totalPrice / Math.max(1, item.quote.nights)
        : Number(item.listing.weekdayPrice) || 0,
    }));

  const searchBar = <StaySearchBar initial={state} onSearch={update} variant="compact" />;

  return (
    <>
      {navSlot ? createPortal(searchBar, navSlot) : null}
      <Toolbar>
        {!navSlot && <div style={{ marginBottom: 12 }}>{searchBar}</div>}
        <CategoryBar
          value={state.filters.category}
          onChange={category => update({ ...state, filters: { ...state.filters, category } })}
          trailing={
            <FiltersButton
              type="button"
              onClick={() => setFiltersOpen(true)}
              data-testid="stay-filters-open"
              aria-label={filterCount ? `Filters (${filterCount})` : 'Filters'}
            >
              <FiltersGlyph />
              Filters
              {filterCount > 0 && <em>{filterCount}</em>}
            </FiltersButton>
          }
        />
      </Toolbar>
      <Layout>
        <ListColumn $hiddenOnPhone={showMap}>
          <ResultsHead>
            <Count data-testid="stay-search-count">
              {result ? resultsHeading(result.total, state.place, Boolean(state.bounds)) : ' '}
            </Count>
            <SortSelect
              aria-label="Sort"
              value={state.sortBy}
              onChange={e => update({ ...state, sortBy: e.target.value as StaySortOption })}
            >
              <option value={StaySortOption.Recommended}>Recommended</option>
              <option value={StaySortOption.PriceLowToHigh}>Price: low to high</option>
              <option value={StaySortOption.PriceHighToLow}>Price: high to low</option>
              <option value={StaySortOption.Rating}>Top rated</option>
            </SortSelect>
          </ResultsHead>
          {error && !data && (
            <Alert tone="error" title="Could not search">
              Try again in a moment.
            </Alert>
          )}
          {loading && !data ? (
            <LoadingBlock label="Finding places" />
          ) : items.length === 0 ? (
            <EmptyState
              title="No exact matches"
              description="Try changing or removing some of your filters or dates, or zoom out on the map."
              action={
                filterCount ? (
                  <Button
                    type="button"
                    theme="secondary"
                    text="Remove filters"
                    onClick={() =>
                      update({
                        ...state,
                        filters: { ...EMPTY_FILTERS, category: state.filters.category },
                      })
                    }
                  />
                ) : undefined
              }
            />
          ) : (
            <ResultsGrid>
              {items.map(item => (
                <StayCard
                  key={item.listing.id}
                  listing={item.listing}
                  quote={item.quote}
                  dates={{ checkIn: state.checkIn, checkOut: state.checkOut }}
                  onOpen={() => openStay(item.listing.id)}
                  onHover={hovering => setHoveredId(hovering ? item.listing.id : null)}
                />
              ))}
            </ResultsGrid>
          )}
          {result && (result.hasMore || page > 1) && (
            <Pager>
              <Button
                type="button"
                theme="secondary"
                text="Previous"
                isDisabled={page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              />
              <Muted $size="sm">Page {page}</Muted>
              <Button
                type="button"
                theme="secondary"
                text="Next"
                isDisabled={!result.hasMore}
                onClick={() => setPage(p => p + 1)}
              />
            </Pager>
          )}
        </ListColumn>
        <MapColumn $shownOnPhone={showMap}>
          <MapFrame>
            <ResultsMap
              results={markers}
              center={
                state.latitude !== undefined && state.longitude !== undefined && !state.bounds
                  ? { lat: state.latitude, lng: state.longitude }
                  : undefined
              }
              hoveredId={hoveredId}
              onSearchArea={bounds => update({ ...state, bounds })}
              onOpen={openStay}
            />
          </MapFrame>
        </MapColumn>
      </Layout>
      <MapToggle type="button" onClick={() => setShowMap(value => !value)}>
        {showMap ? 'Show list' : 'Show map'}
      </MapToggle>
      <FiltersModal
        open={filtersOpen}
        value={state.filters}
        onClose={() => setFiltersOpen(false)}
        onApply={filters => update({ ...state, filters })}
      />
    </>
  );
}
